import type { RootState } from '../index'
import type { UserWorkspace } from '../auth/authSlice'

export const selectWorkspace = (state: RootState) => state.workspace.workspace
export const selectStatuses = (state: RootState) => state.workspace.statuses
export const selectLabels = (state: RootState) => state.workspace.labels
export const selectMembers = (state: RootState) => state.workspace.members
export const selectMembersInitialized = (state: RootState) => state.workspace.membersInitialized
export const selectWorkspaceLoading = (state: RootState) => state.workspace.loading
export const selectWorkspaceError = (state: RootState) => state.workspace.error
export const selectCreateWorkspace = (state: RootState) => state.workspace.createWorkspace
export const selectWorkspaceInvite = (state: RootState) => state.workspace.invite
// Pro access = planStatus active|canceling (cancel-at-period-end keeps access until the
// period ends). Falls back to the legacy planId === 'pro' for workspaces not yet migrated
// to planStatus (SCD-987).
const workspaceHasPro = (ws: RootState['workspace']['workspace']): boolean => {
    const status = ws?.planStatus
    if (status) return status === 'active' || status === 'canceling'
    return ws?.planId === 'pro'
}

export const selectIsWorkspacePro = (state: RootState) => workspaceHasPro(state.workspace.workspace)

// True when the workspace is a real (persisted) workspace, not the guest boilerplate
export const selectIsWorkspaceNotBoilerplate = (state: RootState) =>
    Boolean(state.workspace.workspace?.createdBy)

// Feature flags derived from workspace state
export const selectCanDeleteProjectList = (state: RootState) =>
    selectIsWorkspaceNotBoilerplate(state)

export const selectCanTransferList = (state: RootState) =>
    selectIsWorkspaceNotBoilerplate(state)

export const selectCanProjectOption = (state: RootState) =>
    selectIsWorkspaceNotBoilerplate(state)

export const selectFigmaToken = (state: RootState) => state.workspace.workspace?.figmaAccessToken

export const selectSlackConnection = (state: RootState) => ({
    connected: !!state.workspace.workspace?.slackConnected,
    teamName: state.workspace.workspace?.slackTeamName ?? '',
    notifyChannelName: state.workspace.workspace?.slackNotifyChannelName ?? '',
})


const FREE_STORAGE_LIMIT_BYTES = 1 * 1024 * 1024 * 1024   // 1 GB
const PRO_STORAGE_LIMIT_BYTES = 100 * 1024 * 1024 * 1024  // 100 GB

export const selectStorageUsedBytes = (state: RootState) =>
    state.workspace.workspace?.storageUsedBytes ?? 0

export const selectStorageLimitBytes = (state: RootState) => {
    const isPro = workspaceHasPro(state.workspace.workspace)
    return isPro ? PRO_STORAGE_LIMIT_BYTES : FREE_STORAGE_LIMIT_BYTES
}

export const selectIsStorageFull = (state: RootState) => {
    const used = selectStorageUsedBytes(state)
    const limit = selectStorageLimitBytes(state)
    return limit !== null && used >= limit
}

export const selectIsCurrentUserOwner = (state: RootState) => {
    const workspaceId = state.workspace.workspace?.id
    return state.auth?.workspaces.find((w: UserWorkspace) => w.id === workspaceId)?.role === 'owner'
}

export const selectUsageStats = (state: RootState) => state.workspace.usageStats
